/*
 * SipStack on SIP.js (MIT) over WSS, media through the browser's
 * RTCPeerConnection (Opus, DTLS-SRTP, ICE). One SIP.js UserAgent per account.
 */
import {
  Invitation,
  Inviter,
  Registerer,
  RegistererState,
  Session,
  SessionState,
  UserAgent,
  Web,
} from "sip.js";
import type { IncomingRequestMessage } from "sip.js/lib/core";
import type {
  AccountListener,
  CallListener,
  SipStack,
  StackAccount,
  StackCall,
  StackCallState,
} from "./sip";
import type { AccountConfig } from "./types";

export interface MediaPrefs {
  inputDeviceId?: string;
  outputDeviceId?: string;
  /** browser echo cancellation (managed setting audio.echo_cancellation) */
  echoCancellation?: boolean;
}

export class SipJsStack implements SipStack {
  readonly media: MediaPrefs = {};
  private calls = new Set<SipJsCall>();

  constructor(
    private wssUrl: string,
    private iceServers: RTCIceServer[] = [],
    private userAgentString = "MocTel Vox Web/0.1.0",
  ) {}

  private uas = new Set<UserAgent>();

  addAccount(cfg: AccountConfig, l: AccountListener): StackAccount {
    return new SipJsAccount(this, cfg, l);
  }

  setIceServers(servers: RTCIceServer[]): void {
    this.iceServers = servers;
    for (const ua of this.uas) {
      const o = ua.configuration.sessionDescriptionHandlerFactoryOptions as {
        peerConnectionConfiguration?: RTCConfiguration;
      };
      o.peerConnectionConfiguration = { ...o.peerConnectionConfiguration, iceServers: servers };
    }
  }

  forget(ua: UserAgent): void {
    this.uas.delete(ua);
  }

  ua(cfg: AccountConfig, onInvite: (inv: Invitation) => void): UserAgent {
    const uri = UserAgent.makeURI(cfg.uri);
    if (!uri) throw new Error("invalid uri");
    const ua = new UserAgent({
      uri,
      displayName: cfg.display_name,
      authorizationUsername: cfg.auth_user ?? uri.user,
      authorizationPassword: cfg.password,
      authorizationHa1: cfg.ha1,
      userAgentString: this.userAgentString,
      transportOptions: { server: this.wssUrl, keepAliveInterval: 25 },
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: { iceServers: this.iceServers },
        iceGatheringTimeout: 1000,
      },
      logLevel: "warn",
      delegate: { onInvite },
    });
    this.uas.add(ua);
    return ua;
  }

  constraints(): MediaStreamConstraints {
    const id = this.media.inputDeviceId;
    const audio: MediaTrackConstraints = {};
    if (id) audio.deviceId = { exact: id };
    if (this.media.echoCancellation !== undefined) audio.echoCancellation = this.media.echoCancellation;
    return { audio: Object.keys(audio).length ? audio : true, video: false };
  }

  /** Switch output (setSinkId) for every call now and for new ones. */
  async setOutputDevice(id: string | undefined): Promise<void> {
    this.media.outputDeviceId = id;
    for (const c of this.calls) await c.applySink();
  }

  /** Switch the microphone of every call now and of new ones. */
  async setInputDevice(id: string | undefined): Promise<void> {
    this.media.inputDeviceId = id;
    for (const c of this.calls) await c.switchInput().catch(() => undefined);
  }

  register(c: SipJsCall): void {
    this.calls.add(c);
  }

  unregisterCall(c: SipJsCall): void {
    this.calls.delete(c);
  }
}

class SipJsAccount implements StackAccount {
  private ua: UserAgent;
  private registerer?: Registerer;
  private started?: Promise<void>;

  constructor(
    private stack: SipJsStack,
    private cfg: AccountConfig,
    private l: AccountListener,
  ) {
    this.ua = stack.ua(cfg, (inv) => this.incoming(inv));
    this.ua.transport.onDisconnect = (err?: Error) => {
      if (err) this.l.onRegState("failed", "connection lost");
    };
  }

  private start(): Promise<void> {
    this.started ??= this.ua.start();
    return this.started;
  }

  register(): void {
    void (async () => {
      try {
        await this.start();
        this.registerer ??= new Registerer(this.ua, { expires: this.cfg.expires ?? 600 });
        this.registerer.stateChange.addListener((s) => {
          if (s === RegistererState.Registered) this.l.onRegState("registered", "200 OK");
          if (s === RegistererState.Unregistered) this.l.onRegState("unregistered");
        });
        this.l.onRegState("registering");
        await this.registerer.register({
          requestDelegate: {
            onReject: (res) =>
              this.l.onRegState("failed", `${res.message.statusCode} ${res.message.reasonPhrase}`),
          },
        });
      } catch (e) {
        this.l.onRegState("failed", String(e));
      }
    })();
  }

  async unregister(): Promise<void> {
    if (!this.registerer) return;
    this.l.onRegState("unregistering");
    await this.registerer.unregister().catch(() => undefined);
  }

  complete(target: string): string {
    if (/^sips?:/i.test(target)) return target;
    const host = UserAgent.makeURI(this.cfg.uri)?.host ?? "";
    return `sip:${target}@${host}`;
  }

  dial(target: string, l: CallListener, headers?: string[]): StackCall {
    const uri = UserAgent.makeURI(target);
    if (!uri) throw new Error("invalid target");
    const inviter = new Inviter(this.ua, uri, {
      sessionDescriptionHandlerOptions: { constraints: this.stack.constraints() },
      // play what the far end sends before it answers (ringback, announcements),
      // and have ICE and DTLS done by then: PSTN calls often start talking
      // before the 200 OK gets here, and those first words were lost
      earlyMedia: true,
      ...(headers?.length ? { extraHeaders: headers } : {}),
    });
    const call = new SipJsCall(this.stack, inviter, target, undefined);
    call.listen(l);
    void inviter
      .invite({
        requestDelegate: {
          onProgress: (res) => {
            const code = res.message.statusCode;
            if (code === 180) call.progress("ringing");
            else if (code === 183 && res.message.body) call.progress("early");
            // early audio comes in a 183, sometimes in a 180
            if (res.message.body) call.attachEarlyMedia();
          },
          onReject: (res) => call.rejected(res.message.statusCode ?? 500, res.message.reasonPhrase ?? ""),
        },
      })
      .catch((e) => call.rejected(500, String(e)));
    return call;
  }

  private incoming(inv: Invitation): void {
    const peer = inv.remoteIdentity.uri.toString();
    const call = new SipJsCall(this.stack, inv, peer, inv.remoteIdentity.displayName || undefined, xHeaders(inv.request));
    this.l.onIncoming(call);
  }

  setCredentials(authUser: string, secret: { password?: string; ha1?: string }): void {
    // SIP.js reads these at each challenge (UserAgent's authentication factory)
    const o = this.ua.configuration;
    o.authorizationUsername = authUser;
    o.authorizationPassword = secret.password ?? "";
    o.authorizationHa1 = secret.ha1 ?? "";
  }

  async dispose(): Promise<void> {
    await this.unregister();
    await this.ua.stop().catch(() => undefined);
    this.stack.forget(this.ua);
  }
}

class SipJsCall implements StackCall {
  readonly id: string;
  private l?: CallListener;
  private audio?: HTMLAudioElement;
  private micOn = true;
  private playOn = true;
  private mic?: MediaStreamTrack;
  private ended = false;

  constructor(
    private stack: SipJsStack,
    private session: Session,
    readonly peer: string,
    readonly peerName: string | undefined,
    readonly headers?: Record<string, string>,
  ) {
    this.id = (session as unknown as { request: { callId: string } }).request.callId;
    stack.register(this);
    session.stateChange.addListener((s) => this.onSession(s));
    session.delegate = {
      onInvite: (req: IncomingRequestMessage) => this.onReinvite(req),
      onRefer: (referral) => {
        void referral.accept().then(() => this.l?.onReferred?.(referral.referTo.uri.toString()));
      },
    };
  }

  listen(l: CallListener): void {
    this.l = l;
  }

  progress(s: StackCallState): void {
    this.l?.onState(s);
  }

  /** an early answer (183 with SDP): its audio plays before the call connects */
  attachEarlyMedia(): void {
    if (!this.ended) this.attachMedia();
  }

  rejected(code: number, reason: string): void {
    this.finish(reason, code);
  }

  private onSession(s: SessionState): void {
    if (s === SessionState.Established) {
      this.attachMedia();
      this.l?.onState("established");
    } else if (s === SessionState.Terminated) {
      this.finish(this.ended ? undefined : "terminated");
    }
  }

  private finish(reason?: string, code?: number): void {
    if (this.ended) return;
    this.ended = true;
    this.audio?.remove();
    this.stack.unregisterCall(this);
    this.l?.onState("ended", reason, code);
  }

  private onReinvite(req: IncomingRequestMessage): void {
    const body = req.body ?? "";
    const m = /^a=(sendonly|inactive|recvonly|sendrecv)\s*$/m.exec(body);
    this.l?.onRemoteHold(m ? m[1] === "sendonly" || m[1] === "inactive" : false);
  }

  private sdh(): Web.SessionDescriptionHandler | undefined {
    return this.session.sessionDescriptionHandler as Web.SessionDescriptionHandler | undefined;
  }

  private attachMedia(): void {
    const sdh = this.sdh();
    if (!sdh) return;
    this.mic = sdh.localMediaStream.getAudioTracks()[0];
    if (!this.audio) {
      this.audio = document.createElement("audio");
      this.audio.autoplay = true;
      this.audio.dataset.call = this.id;
      this.audio.style.display = "none";
      document.body.appendChild(this.audio);
    }
    this.audio.srcObject = sdh.remoteMediaStream;
    this.audio.muted = !this.playOn;
    void this.applySink();
    this.applyMic();
  }

  async applySink(): Promise<void> {
    const id = this.stack.media.outputDeviceId;
    const a = this.audio as HTMLAudioElement & { setSinkId?(id: string): Promise<void> };
    if (a && id !== undefined && typeof a.setSinkId === "function") {
      await a.setSinkId(id).catch(() => undefined);
    }
  }

  private applyMic(): void {
    for (const t of this.sdh()?.localMediaStream.getAudioTracks() ?? []) t.enabled = this.micOn;
  }

  async answer(): Promise<void> {
    if (!(this.session instanceof Invitation)) throw new Error("not incoming");
    await this.session.accept({
      sessionDescriptionHandlerOptions: { constraints: this.stack.constraints() },
    });
  }

  async hangup(code?: number): Promise<void> {
    const s = this.session;
    switch (s.state) {
      case SessionState.Initial:
      case SessionState.Establishing:
        if (s instanceof Inviter) await s.cancel();
        else if (s instanceof Invitation) await s.reject({ statusCode: code ?? 486 });
        break;
      case SessionState.Established:
        await s.bye();
        break;
      default:
        break;
    }
  }

  setHold(hold: boolean): Promise<void> {
    return new Promise((resolve, reject) => {
      const s = this.session as Session & { sessionDescriptionHandlerOptionsReInvite: object };
      s.sessionDescriptionHandlerOptionsReInvite = { hold } as Web.SessionDescriptionHandlerOptions;
      s.invite({
        requestDelegate: {
          onAccept: () => resolve(),
          onReject: (res) => reject(new Error(`${res.message.statusCode}`)),
        },
      }).catch(reject);
    });
  }

  setMicEnabled(on: boolean): void {
    this.micOn = on;
    this.applyMic();
  }

  setPlayback(on: boolean): void {
    this.playOn = on;
    if (this.audio) this.audio.muted = !on;
  }

  sendDtmf(digits: string): void {
    const sdh = this.sdh();
    if (!sdh || !sdh.sendDtmf(digits, { duration: 120, interToneGap: 80 })) {
      throw new Error("dtmf not available");
    }
  }

  async refer(to: string): Promise<void> {
    const uri = UserAgent.makeURI(to);
    if (!uri) throw new Error("invalid target");
    await this.session.refer(uri);
  }

  peerConnection(): RTCPeerConnection | undefined {
    return this.sdh()?.peerConnection;
  }

  async replaceOutgoing(track: MediaStreamTrack | null): Promise<void> {
    const sender = this.peerConnection()?.getSenders().find((s) => s.track?.kind === "audio" || s.track === null);
    if (sender) await sender.replaceTrack(track ?? this.mic ?? null);
  }

  /** New microphone from the current constraints; a conference mix keeps its own track. */
  async switchInput(): Promise<void> {
    const sdh = this.sdh();
    const old = this.mic;
    if (!sdh || !old) return;
    const [track] = (await navigator.mediaDevices.getUserMedia(this.stack.constraints())).getAudioTracks();
    const sender = this.peerConnection()?.getSenders().find((x) => x.track === old);
    if (sender) await sender.replaceTrack(track);
    sdh.localMediaStream.removeTrack(old);
    sdh.localMediaStream.addTrack(track);
    old.stop();
    this.mic = track;
    this.applyMic();
  }

  remoteStream(): MediaStream | undefined {
    return this.sdh()?.remoteMediaStream;
  }

  localStream(): MediaStream | undefined {
    return this.sdh()?.localMediaStream;
  }
}

/* The X- headers of an incoming INVITE (what the PBX adds: dialed number,
 * queue...), names in lower case. Caller-controlled text: bounded, and only
 * ever shown or handed over as data. */
export function xHeaders(req: IncomingRequestMessage): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  let n = 0;
  for (const name of Object.keys(req.headers)) {
    if (!/^x-[a-z0-9-]{1,48}$/i.test(name) || n >= 20) continue;
    const v = req.getHeader(name);
    if (v === undefined) continue;
    out[name.toLowerCase()] = v.slice(0, 256);
    n++;
  }
  return n ? out : undefined;
}
